import { createFileRoute, notFound } from "@tanstack/react-router";
import { cityFromSlug, specialtyFromSlug, insuranceFromSlug } from "@/lib/seo-slugs";
import { getIntentProviders } from "@/lib/seo-intent.functions";
import { decideIndexability, robotsMeta } from "@/lib/seo-indexability";
import { IntentPageShell } from "@/components/seo/IntentPageShell";
import { setSeoPageCache } from "@/lib/page-cache";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/specialty/$specialty/$city/$insurance/available-today")({
 staleTime: 10 * 60_000,
 loader: async ({ params }) => {
 await setSeoPageCache();
 const specialty = specialtyFromSlug(params.specialty);
 const city = cityFromSlug(params.city);
 const insurance = insuranceFromSlug(params.insurance);
 if (!specialty || !city || !insurance) throw notFound();
 const cityName = city.split(",")[0].trim();
 const state = (city.split(",")[1] ?? "").trim();
 const { providers, total } = await getIntentProviders({
 data: { specialty, city: cityName, state: state || undefined, kind: "available_today" },
 });
 return { specialty, city, cityName, insurance, providers, total };
 },
 head: ({ params, loaderData }) => {
 const specialty = loaderData?.specialty ?? "Providers";
 const city = loaderData?.city ?? "";
 const ins = loaderData?.insurance?.short ?? params.insurance;
 const total = loaderData?.total ?? 0;
 const url = `${SITE}/specialty/${params.specialty}/${params.city}/${params.insurance}/available-today`;
 const baseUrl = `${SITE}/specialty/${params.specialty}/${params.city}`;
 const title = `${specialty} available today in ${city}, accepts ${ins} | Heallexa`;
 const description = `Same-day ${specialty.toLowerCase()} appointments in ${city} who accept ${ins}. Compare in-network providers with open availability today and book online — free, no signup, on Heallexa.`;
 const robots = decideIndexability({ providerCount: total, hasRelatedProviders: total > 0, internalLinkCount: 1 });
 const canonical = robots.index ? url : baseUrl;
 return {
 meta: [
 { title }, { name: "description", content: description },
 { property: "og:title", content: title }, { property: "og:description", content: description },
 { property: "og:url", content: canonical }, robotsMeta(robots.robots),
 ],
 links: [{ rel: "canonical", href: canonical }],
 };
 },
 component: () => {
 const { specialty, city, cityName, insurance, providers } = Route.useLoaderData();
 const ins = insurance?.short ?? "";
 return (
 <IntentPageShell
 title={`${specialty} available today in ${city}, ${ins}`}
 description=""
 h1={`${specialty} available today in ${cityName}, accepts ${ins}`}
 intro={`${specialty} in ${city} with open availability today who accept ${ins}. Confirm coverage with the provider's office at booking.`}
 emptyMessage={`No ${specialty.toLowerCase()} matching today's availability and ${ins} were found in ${city}. Try removing the insurance filter.`}
 providers={providers}
 breadcrumbs={[
 { label: "Heallexa", href: "/" },
 { label: specialty },
 { label: cityName },
 { label: ins },
 { label: "Available today" },
 ]}
 />
 );
 },
});
